import type { Address } from 'viem';

import { events, factoryAbi } from './abi';
import { DEPLOYMENT, getActiveChainConfig, getActiveNetworkKey } from './config';
import { getCache, setCache } from './cache';
import { readClient } from './client';

export type MarketActivity = {
  buyVolume: bigint;
  sellVolume: bigint;
  claimVolume: bigint;
  traderCount: number;
  claimCount: number;
  traders: string[];
};

export type MarketOverview = {
  totalMarkets: number;
  resolvableMarkets: number;
  totalBuyVolume: bigint;
  uniqueTraders: number;
};

export type MarketHistoryPoint = {
  blockNumber: bigint;
  logIndex: number;
  timestampMs: number;
  trader: string;
  tradeVolume: bigint;
  yesProbability: number;
  noProbability: number;
  yesBuyFlow: bigint;
  noBuyFlow: bigint;
  yesSellFlow: bigint;
  noSellFlow: bigint;
  buyFlow: bigint;
  sellFlow: bigint;
  netFlow: bigint;
};

const CACHE_TTL_MS = 30_000;
const LOG_CHUNK_INITIAL = 40_000n;
const LOG_CHUNK_MIN = 250n;
const LOG_CHUNK_MAX = 250_000n;

type ReadLogsParams = NonNullable<Parameters<typeof readClient.getLogs>[0]>;

async function getLogsAdaptive(params: ReadLogsParams): Promise<any[]> {
  const fromBlock = params.fromBlock as bigint | undefined;
  if (fromBlock === undefined) {
    return readClient.getLogs(params as any) as Promise<any[]>;
  }

  const explicitToBlock = params.toBlock;
  const latestBlock = explicitToBlock === undefined || explicitToBlock === 'latest'
    ? await readClient.getBlockNumber()
    : (explicitToBlock as bigint);

  if (fromBlock > latestBlock) return [];

  const out: any[] = [];
  let cursor = fromBlock;
  let chunk = LOG_CHUNK_INITIAL;

  while (cursor <= latestBlock) {
    const end = cursor + chunk - 1n > latestBlock ? latestBlock : cursor + chunk - 1n;
    try {
      const rows = await readClient.getLogs({
        ...(params as any),
        fromBlock: cursor,
        toBlock: end,
      });
      out.push(...rows);
      cursor = end + 1n;
      chunk = chunk < LOG_CHUNK_MAX ? (chunk * 2n > LOG_CHUNK_MAX ? LOG_CHUNK_MAX : chunk * 2n) : chunk;
    } catch (error) {
      if (chunk <= LOG_CHUNK_MIN) throw error;
      chunk = chunk / 2n;
      if (chunk < LOG_CHUNK_MIN) chunk = LOG_CHUNK_MIN;
    }
  }

  return out;
}

function invertGrossSellOutput(netOut: bigint, sellFeeBps: bigint): bigint {
  if (sellFeeBps === 0n) return netOut;
  const denominator = 10_000n - sellFeeBps;
  let estimate = (netOut * 10_000n) / denominator;
  for (let candidate = estimate; candidate <= estimate + 8n; candidate += 1n) {
    const fee = (candidate * sellFeeBps) / 10_000n;
    if (candidate - fee === netOut) return candidate;
  }
  return estimate;
}

export async function getMarketHistory(
  address: Address,
  currentYesReserve: bigint,
  currentNoReserve: bigint,
  collateralAddress?: string,
): Promise<MarketHistoryPoint[]> {
  const chain = getActiveChainConfig();
  const effectiveCollateral = (collateralAddress || DEPLOYMENT.collateral) as Address;
  const cacheKey = `history:${getActiveNetworkKey()}:${address}`;
  const cached = getCache<MarketHistoryPoint[]>(cacheKey, CACHE_TTL_MS);
  if (cached) return cached;

  const [feeConfigRead, buyLogs, sellLogs] = await Promise.all([
    readClient.readContract({
      address: DEPLOYMENT.factory as Address,
      abi: factoryAbi,
      functionName: 'feeConfig',
    }),
    getLogsAdaptive({
      address,
      event: events.bought,
      fromBlock: chain.deploymentStartBlock,
      toBlock: 'latest',
    }),
    getLogsAdaptive({
      address,
      event: events.sold,
      fromBlock: chain.deploymentStartBlock,
      toBlock: 'latest',
    }),
  ]);

  const [, buyFeeBpsRaw, sellFeeBpsRaw] = feeConfigRead as [Address, number | bigint, number | bigint];
  const buyFeeBps = BigInt(Number(buyFeeBpsRaw));
  const sellFeeBps = BigInt(Number(sellFeeBpsRaw));

  const combined = [
    ...buyLogs.map((log) => ({ type: 'buy' as const, log })),
    ...sellLogs.map((log) => ({ type: 'sell' as const, log })),
  ].sort((a, b) => {
    if (a.log.blockNumber === b.log.blockNumber) {
      return Number((a.log.logIndex ?? 0) - (b.log.logIndex ?? 0));
    }
    return a.log.blockNumber < b.log.blockNumber ? -1 : 1;
  });

  if (combined.length === 0) {
    setCache(cacheKey, []);
    return [];
  }

  const tradeTxHashes = new Set(
    combined
      .map((entry) => entry.log.transactionHash)
      .filter((hash): hash is string => Boolean(hash)),
  );

  const firstTradeBlock = combined[0].log.blockNumber;
  const [inboundCollateralLogs, outboundCollateralLogs] = await Promise.all([
    getLogsAdaptive({
      address: effectiveCollateral,
      event: events.transfer,
      args: { to: address },
      fromBlock: firstTradeBlock,
      toBlock: 'latest',
    }),
    getLogsAdaptive({
      address: effectiveCollateral,
      event: events.transfer,
      args: { from: address },
      fromBlock: firstTradeBlock,
      toBlock: 'latest',
    }),
  ]);

  const inboundByTxAndFrom = new Map<string, bigint>();
  for (const log of inboundCollateralLogs) {
    if (!log.transactionHash || !tradeTxHashes.has(log.transactionHash)) continue;
    const from = ((log.args.from as Address) || '').toLowerCase();
    const key = `${log.transactionHash}:${from}`;
    const value = (log.args.value as bigint) || 0n;
    inboundByTxAndFrom.set(key, (inboundByTxAndFrom.get(key) ?? 0n) + value);
  }

  const outboundByTxAndTo = new Map<string, bigint>();
  for (const log of outboundCollateralLogs) {
    if (!log.transactionHash || !tradeTxHashes.has(log.transactionHash)) continue;
    const to = ((log.args.to as Address) || '').toLowerCase();
    const key = `${log.transactionHash}:${to}`;
    const value = (log.args.value as bigint) || 0n;
    outboundByTxAndTo.set(key, (outboundByTxAndTo.get(key) ?? 0n) + value);
  }

  const uniqueBlockNumbers = Array.from(new Set(combined.map((entry) => entry.log.blockNumber.toString()))).map((value) => BigInt(value));
  const blockTimes = new Map<string, number>();
  const blockRows = await Promise.all(uniqueBlockNumbers.map(async (blockNumber) => {
    const block = await readClient.getBlock({ blockNumber });
    return { blockNumber, timestampMs: Number(block.timestamp) * 1000 };
  }));
  for (const row of blockRows) {
    blockTimes.set(row.blockNumber.toString(), row.timestampMs);
  }

  let yesReserve = currentYesReserve;
  let noReserve = currentNoReserve;
  const reversedPoints: MarketHistoryPoint[] = [];

  for (let index = combined.length - 1; index >= 0; index -= 1) {
    const entry = combined[index];
    const total = yesReserve + noReserve;
    const yesProbability = total > 0n ? Number((yesReserve * 10_000n) / total) / 100 : 50;
    const noProbability = total > 0n ? Number((noReserve * 10_000n) / total) / 100 : 50;

    if (entry.type === 'buy') {
      const isYes = Boolean(entry.log.args.isYes);
      const collateralIn = (entry.log.args.collateralIn as bigint) || 0n;
      const sharesOut = (entry.log.args.sharesOut as bigint) || 0n;
      const trader = (entry.log.args.user as Address) ?? address;
      const timestampMs = blockTimes.get(entry.log.blockNumber.toString()) ?? Date.now();
      const fee = (collateralIn * buyFeeBps) / 10_000n;
      const netIn = collateralIn - fee;
      const buyKey = entry.log.transactionHash ? `${entry.log.transactionHash}:${trader.toLowerCase()}` : '';
      const txVolume = buyKey ? (inboundByTxAndFrom.get(buyKey) ?? collateralIn) : collateralIn;

      reversedPoints.push({
        blockNumber: entry.log.blockNumber,
        logIndex: Number(entry.log.logIndex ?? 0),
        timestampMs,
        trader: trader.toLowerCase(),
        tradeVolume: txVolume,
        yesProbability,
        noProbability,
        yesBuyFlow: isYes ? txVolume : 0n,
        noBuyFlow: isYes ? 0n : txVolume,
        yesSellFlow: 0n,
        noSellFlow: 0n,
        buyFlow: txVolume,
        sellFlow: 0n,
        netFlow: txVolume,
      });

      if (isYes) {
        yesReserve -= netIn;
        noReserve += sharesOut;
      } else {
        noReserve -= netIn;
        yesReserve += sharesOut;
      }
    } else {
      const isYes = Boolean(entry.log.args.isYes);
      const sharesIn = (entry.log.args.sharesIn as bigint) || 0n;
      const collateralOut = (entry.log.args.collateralOut as bigint) || 0n;
      const trader = (entry.log.args.user as Address) ?? address;
      const timestampMs = blockTimes.get(entry.log.blockNumber.toString()) ?? Date.now();
      const grossOut = invertGrossSellOutput(collateralOut, sellFeeBps);
      const sellKey = entry.log.transactionHash ? `${entry.log.transactionHash}:${trader.toLowerCase()}` : '';
      const txVolume = sellKey ? (outboundByTxAndTo.get(sellKey) ?? collateralOut) : collateralOut;

      reversedPoints.push({
        blockNumber: entry.log.blockNumber,
        logIndex: Number(entry.log.logIndex ?? 0),
        timestampMs,
        trader: trader.toLowerCase(),
        tradeVolume: txVolume,
        yesProbability,
        noProbability,
        yesBuyFlow: 0n,
        noBuyFlow: 0n,
        yesSellFlow: isYes ? txVolume : 0n,
        noSellFlow: isYes ? 0n : txVolume,
        buyFlow: 0n,
        sellFlow: txVolume,
        netFlow: -txVolume,
      });

      if (isYes) {
        yesReserve += grossOut;
        noReserve -= sharesIn;
      } else {
        noReserve += grossOut;
        yesReserve -= sharesIn;
      }
    }
  }

  const data = reversedPoints.reverse();
  setCache(cacheKey, data);
  return data;
}

export async function getMarketActivity(address: Address): Promise<MarketActivity> {
  const chain = getActiveChainConfig();
  const cacheKey = `activity:${getActiveNetworkKey()}:${address}`;
  const cached = getCache<MarketActivity>(cacheKey, CACHE_TTL_MS);
  if (cached) return cached;

  const [buyLogs, sellLogs, claimLogs] = await Promise.all([
    getLogsAdaptive({
      address,
      event: events.bought,
      fromBlock: chain.deploymentStartBlock,
      toBlock: 'latest',
    }),
    getLogsAdaptive({
      address,
      event: events.sold,
      fromBlock: chain.deploymentStartBlock,
      toBlock: 'latest',
    }),
    getLogsAdaptive({
      address,
      event: events.claimed,
      fromBlock: chain.deploymentStartBlock,
      toBlock: 'latest',
    }),
  ]);

  const traders = new Set<string>();
  let buyVolume = 0n;
  let sellVolume = 0n;
  let claimVolume = 0n;

  for (const log of buyLogs) {
    traders.add((log.args.user as string).toLowerCase());
    buyVolume += (log.args.collateralIn as bigint) || 0n;
  }
  for (const log of sellLogs) {
    traders.add((log.args.user as string).toLowerCase());
    sellVolume += (log.args.collateralOut as bigint) || 0n;
  }
  for (const log of claimLogs) {
    traders.add((log.args.user as string).toLowerCase());
    claimVolume += (log.args.payout as bigint) || 0n;
  }

  const data: MarketActivity = {
    buyVolume,
    sellVolume,
    claimVolume,
    traderCount: traders.size,
    claimCount: claimLogs.length,
    traders: Array.from(traders),
  };

  setCache(cacheKey, data);
  return data;
}

export async function getMarketActivities(addresses: Address[]): Promise<Map<string, MarketActivity>> {
  const out = new Map<string, MarketActivity>();
  if (addresses.length === 0) return out;

  const chain = getActiveChainConfig();
  const missing: Address[] = [];

  for (const address of addresses) {
    const key = address.toLowerCase();
    if (out.has(key)) continue;

    const cacheKey = `activity:${getActiveNetworkKey()}:${address}`;
    const cached = getCache<MarketActivity>(cacheKey, CACHE_TTL_MS);
    if (cached) {
      out.set(key, cached);
      continue;
    }
    missing.push(address);
  }

  if (missing.length === 0) return out;

  const traderSets = new Map<string, Set<string>>();
  const aggregates = new Map<string, { buyVolume: bigint; sellVolume: bigint; claimVolume: bigint; claimCount: number }>();

  const ensureAggregate = (key: string) => {
    if (!aggregates.has(key)) {
      aggregates.set(key, { buyVolume: 0n, sellVolume: 0n, claimVolume: 0n, claimCount: 0 });
      traderSets.set(key, new Set<string>());
    }
    return aggregates.get(key)!;
  };

  for (const address of missing) {
    ensureAggregate(address.toLowerCase());
  }

  try {
    const [buyLogs, sellLogs, claimLogs] = await Promise.all([
      getLogsAdaptive({
        address: missing,
        event: events.bought,
        fromBlock: chain.deploymentStartBlock,
        toBlock: 'latest',
      }),
      getLogsAdaptive({
        address: missing,
        event: events.sold,
        fromBlock: chain.deploymentStartBlock,
        toBlock: 'latest',
      }),
      getLogsAdaptive({
        address: missing,
        event: events.claimed,
        fromBlock: chain.deploymentStartBlock,
        toBlock: 'latest',
      }),
    ]);

    for (const log of buyLogs) {
      const key = String(log.address).toLowerCase();
      const aggregate = ensureAggregate(key);
      aggregate.buyVolume += (log.args.collateralIn as bigint) || 0n;
      traderSets.get(key)!.add(String(log.args.user || '').toLowerCase());
    }

    for (const log of sellLogs) {
      const key = String(log.address).toLowerCase();
      const aggregate = ensureAggregate(key);
      aggregate.sellVolume += (log.args.collateralOut as bigint) || 0n;
      traderSets.get(key)!.add(String(log.args.user || '').toLowerCase());
    }

    for (const log of claimLogs) {
      const key = String(log.address).toLowerCase();
      const aggregate = ensureAggregate(key);
      aggregate.claimVolume += (log.args.payout as bigint) || 0n;
      aggregate.claimCount += 1;
      traderSets.get(key)!.add(String(log.args.user || '').toLowerCase());
    }
  } catch {
    // Gracefully degrade to zeroed activity if batched logs fail on provider limits.
  }

  for (const address of missing) {
    const key = address.toLowerCase();
    const aggregate = ensureAggregate(key);
    const traders = Array.from(traderSets.get(key) || []);
    const data: MarketActivity = {
      buyVolume: aggregate.buyVolume,
      sellVolume: aggregate.sellVolume,
      claimVolume: aggregate.claimVolume,
      traderCount: traders.length,
      claimCount: aggregate.claimCount,
      traders,
    };

    out.set(key, data);
    setCache(`activity:${getActiveNetworkKey()}:${address}`, data);
  }

  return out;
}

export function summarizeOverview(activities: MarketActivity[], resolvableMarkets: number): MarketOverview {
  const allTraders = new Set<string>();
  let totalBuyVolume = 0n;

  for (const activity of activities) {
    totalBuyVolume += activity.buyVolume;
    for (const trader of activity.traders) allTraders.add(trader);
  }

  return {
    totalMarkets: activities.length,
    resolvableMarkets,
    totalBuyVolume,
    uniqueTraders: allTraders.size,
  };
}
